import Tooltip from '@mui/material/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import React, { useState, useEffect, useRef } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, TextField, FormControl, Select, MenuItem, Menu, Checkbox, ListItemIcon, ListItemText, Typography } from '@mui/material';

import constants from '../../constants';
import TagsFilter from '../Tags/TagsFilter';
import { buildMongoQuery } from '../../utils/filterConfig';
import ViewStateManager from '../../utils/viewStateManager';
import { dateRangeOptions } from '../../utils/quickFiltersConfig';
import { applyFilters, setQuickFilters } from '../../store/Objects';
import { getPipelineById, setPipelineSettingsModal, setCreatePipelineModal, setPipelineBoard, setLoading } from '../../store/Deals';

const QuickFilters = ({ objectType, config, onAdvancedClick, onGetData, isolate = false, initialFilters = null }) => {
  const dispatch = useDispatch();

  // Get dynamic data from Redux
  const { pipelines, currentPipeline } = useSelector(state => state['Deals']);
  const { contacts, companies, deals } = useSelector(state => state['Objects']);
  const { userDetails, currentWorkspace, members } = useSelector(state => state['Settings']);

  // Track if pipelines existed on mount (stale Redux data from previous workspace/navigation)
  const hadStalePipelines = useRef(pipelines?.length > 0);

  // Load saved filter state from ViewStateManager (skip if isolate mode)
  const savedFilters = isolate ? {} : ViewStateManager.getQuickFilters(objectType);

  // Search state
  const [searchDebounce, setSearchDebounce] = useState(null);
  const [searchText, setSearchText] = useState(savedFilters['searchText'] || '');

  // Dropdown states
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [dropdownSearches, setDropdownSearches] = useState({});
  const [dropdownValues, setDropdownValues] = useState(savedFilters['dropdownValues'] || {});

  // Checkbox filter states
  const [checkboxAnchors, setCheckboxAnchors] = useState({});
  const [checkboxSearches, setCheckboxSearches] = useState({});
  const [checkboxValues, setCheckboxValues] = useState(savedFilters['checkboxValues'] || {});

  // Date filter states
  const [dateAnchors, setDateAnchors] = useState({});
  const [dateValues, setDateValues] = useState(savedFilters['dateValues'] || {});

  // More filters state
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [moreFilterSearches, setMoreFilterSearches] = useState({});
  const [moreFilterValues, setMoreFilterValues] = useState(savedFilters['moreFilterValues'] || {});
  const [moreFilterAnchors, setMoreFilterAnchors] = useState(savedFilters['moreFilterAnchors'] || {});

  // Track initial render
  const [isInitialRender, setIsInitialRender] = useState(true);

  // Show loader immediately if we don't have pipelines yet
  useEffect(() => {
    if (!pipelines?.length && objectType === 'deals') dispatch(setLoading(true));
  }, []);

  // Track if we've restored filters
  const [hasRestoredFilters, setHasRestoredFilters] = useState(false);

  // In isolate mode, restore all filter values from initialFilters prop
  useEffect(() => {
    if (isolate && initialFilters?.filterGroups?.length > 0) {
      const newCheckboxValues = {};
      const newDateValues = {};
      const newMoreFilterValues = {};

      // Build field-to-key mappings from config
      const checkboxFieldMap = {};
      config.checkboxFilters?.forEach(c => { checkboxFieldMap[c.field] = c.key; });

      const dateFieldMap = {};
      config.dateFilters?.forEach(d => { dateFieldMap[d.field] = d.key; });

      const moreFieldMap = {};
      if (config.moreFilters) {
        Object.entries(config.moreFilters).forEach(([key, m]) => { moreFieldMap[m.field] = key; });
      }

      initialFilters.filterGroups.forEach(group => {
        (group.filters || []).forEach(filter => {
          const fieldName = filter.property?.name;
          const value = filter.value;

          // Check if it's a checkbox filter (tags, owner, source, industry, etc.)
          const checkboxKey = checkboxFieldMap[fieldName];
          if (checkboxKey && value && typeof value === 'object')
            newCheckboxValues[checkboxKey] = { ...value };

          // Check if it's a date filter
          const dateKey = dateFieldMap[fieldName];
          if (dateKey && value) newDateValues[dateKey] = value;

          // Check if it's a more filter
          const moreKey = moreFieldMap[fieldName];
          if (moreKey && value && typeof value === 'object')
            newMoreFilterValues[moreKey] = { ...value };
        });
      });

      // Update states if we found values
      if (Object.keys(newDateValues).length > 0) setDateValues(newDateValues);
      if (Object.keys(newCheckboxValues).length > 0) setCheckboxValues(newCheckboxValues);
      if (Object.keys(newMoreFilterValues).length > 0) setMoreFilterValues(newMoreFilterValues);
    } else if (isolate && initialFilters && (!initialFilters.filterGroups || initialFilters.filterGroups.length === 0)) {
      // Clear all filter states when initialFilters is empty (e.g., Clear All clicked)
      setDateValues({});
      setCheckboxValues({});
      setMoreFilterValues({});
    }
  }, [isolate, initialFilters, config]);

  // Fetch properties on mount
  // useEffect(() => { if (objectType) dispatch(getProperties()); }, [objectType]);

  // Save filter state to ViewStateManager whenever it changes (skip if isolate mode)
  useEffect(() => {
    if (!isInitialRender && !isolate) {
      // Only save moreFilterAnchors keys (not the actual DOM elements)
      const moreFilterAnchorKeys = {};
      Object.keys(moreFilterAnchors).forEach(key => {
        moreFilterAnchorKeys[key] = 'pending'; // Just save that this filter is active
      });

      ViewStateManager.setQuickFilters(objectType, {
        searchText,
        dateValues,
        dropdownValues,
        checkboxValues,
        moreFilterValues,
        moreFilterAnchors: moreFilterAnchorKeys
      });
    }
  }, [searchText, dropdownValues, checkboxValues, dateValues, moreFilterValues, moreFilterAnchors, isInitialRender, objectType, isolate]);

  // Debounced search
  useEffect(() => {
    if (isInitialRender) {
      setTimeout(() => {
        setIsInitialRender(false);
        // Apply saved filters immediately after initial render guard clears
        if (savedFilters['searchText'] || Object.keys(savedFilters['dropdownValues'] || {}).length || Object.keys(savedFilters['checkboxValues'] || {}).length) applyQuickFilters(false);
      }, 100);
      return;
    }

    if (searchDebounce) clearTimeout(searchDebounce);
    const timeout = setTimeout(() => { applyQuickFilters(false); }, 500);
    setSearchDebounce(timeout);
    return () => clearTimeout(timeout);
  }, [searchText]);

  // Apply filters when any filter value changes
  useEffect(() => {
    if (isInitialRender) {
      setTimeout(() => { setIsInitialRender(false); }, 100);
      return;
    }

    applyQuickFilters(false);
  }, [dropdownValues, checkboxValues, dateValues, moreFilterValues]);


  // Set pipeline from saved state or first available
  useEffect(() => {
    if (pipelines?.length > 0) {
      // Check if current selection exists in pipelines list
      const currentSelectionExists = dropdownValues['pipeline'] && pipelines.some(p => p['_id'] === dropdownValues['pipeline']);

      if (!dropdownValues['pipeline'] || !currentSelectionExists) {
        // Pipeline not found in current list (likely workspace changed) - set correct one
        // The [dropdownValues] useEffect will fire applyQuickFilters after state updates
        setDropdownValues(prev => ({ ...prev, pipeline: pipelines[0]['_id'] }));
        setIsInitialRender(false);
      } else if (hadStalePipelines['current']) {
        // First fire with stale Redux data from previous navigation - skip request
        // Wait for fresh pipelines from GET /api/deals/pipelines to arrive
        hadStalePipelines['current'] = false;
        setIsInitialRender(false);
      } else {
        // Fresh pipelines loaded or subsequent update - safe to apply filters
        setTimeout(() => { applyQuickFilters(true) }, 100);
      }
    }
  }, [pipelines]);

  // Build and apply filters
  const applyQuickFilters = (includeInitialPipeline = false) => {
    const filterGroups = [], filters = [];

    // Get current dropdown values
    let currentDropdownValues = { ...dropdownValues };

    // Add search filter
    if (searchText && config.search) {
      const searchFields = Array.isArray(config['search']['field']) ? config['search']['field'] : [config['search']['field']];
      const searchFilters = searchFields.map(field => ({ property: { name: field, type: 'text' }, operator: { value: 'contains', label: 'contains exactly', requiresValue: true }, value: searchText }));

      if (searchFilters.length > 1) filterGroups.push({ operator: 'OR', filters: searchFilters });
      else filters.push(...searchFilters);
    }

    // Add dropdown filters
    Object.entries(currentDropdownValues).forEach(([key, value]) => {
      if (value && value !== '') {
        const dropdownConfig = config['dropdowns']?.find(d => d['key'] === key);
        if (dropdownConfig) filters.push({ property: { name: dropdownConfig['field'], type: 'select' }, operator: { value: 'equals', label: 'is equal to', requiresValue: true }, value: value });
      }
    });

    // Add checkbox filters
    Object.entries(checkboxValues).forEach(([key, values]) => {
      const checkedValues = Object.entries(values).filter(([_, checked]) => checked).map(([value]) => value);
      if (checkedValues.length > 0) {
        const checkboxConfig = config['checkboxFilters']?.find(f => f['key'] === key);
        if (checkboxConfig) {
          // Special handling for owner filter with unassigned option
          if (key === 'owner' && checkedValues.includes('')) {
            const hasUnassigned = checkedValues.includes('');
            const assignedValues = checkedValues.filter(v => v !== '');

            if (hasUnassigned && assignedValues.length > 0) {
              // Both unassigned and assigned owners selected
              filterGroups.push({
                operator: 'OR',
                filters: [
                  { property: { name: checkboxConfig['field'], type: 'select' }, operator: { value: 'is_unknown', label: 'is unknown', requiresValue: false } },
                  { property: { name: checkboxConfig['field'], type: 'select' }, operator: { value: 'equals', label: 'is equal to any of', requiresValue: true, multiValue: true }, value: assignedValues.reduce((acc, val) => ({ ...acc, [val]: true }), {}) }
                ]
              });
            } else if (hasUnassigned) {
              // Only unassigned selected
              filters.push({ property: { name: checkboxConfig['field'], type: 'select' }, operator: { value: 'is_unknown', label: 'is unknown', requiresValue: false } });
            }
          } else if (key === 'tags') {
            // Special handling for tags filter - send just 'tags' field, backend will handle both formats
            filters.push({
              property: { name: 'tags', type: 'select' },
              operator: { value: 'equals', label: 'is equal to any of', requiresValue: true, multiValue: true },
              value: checkedValues.reduce((acc, val) => ({ ...acc, [val]: true }), {})
            });
          } else {
            // Regular checkbox filter
            filters.push({ property: { name: checkboxConfig['field'], type: 'select' }, operator: { value: 'equals', label: 'is equal to any of', requiresValue: true, multiValue: true }, value: checkedValues.reduce((acc, val) => ({ ...acc, [val]: true }), {}) });
          }
        }
      }
    });

    // Add date filters
    Object.entries(dateValues).forEach(([key, range]) => {
      if (range) {
        const dateConfig = config['dateFilters']?.find(f => f['key'] === key);
        if (dateConfig) filters.push(getDateRangeFilter(dateConfig['field'], range));
      }
    });

    // Add more filters
    Object.entries(moreFilterValues).forEach(([key, values]) => {
      const checkedValues = Object.entries(values).filter(([_, checked]) => checked).map(([value]) => value);
      if (checkedValues.length > 0) {
        const moreFilterConfig = config['moreFilters']?.[key];
        if (moreFilterConfig) {
          if (moreFilterConfig['type'] === 'number') {
            // For number filters, use between operator
            const numValues = checkedValues.map(v => parseFloat(v)).filter(v => !isNaN(v));
            if (numValues.length === 1) filters.push({ property: { name: moreFilterConfig['field'], type: 'number' }, operator: { value: 'equals', label: 'is equal to', requiresValue: true }, value: numValues[0] });
            else if (numValues.length === 2) {
              const [min, max] = numValues.sort((a, b) => a - b);
              filters.push({ property: { name: moreFilterConfig['field'], type: 'number' }, operator: { value: 'between', label: 'is between', requiresValue: true, isBetween: true }, value: { min, max } });
            }
          } else {
            // For text/select filters
            filters.push({ property: { name: moreFilterConfig['field'], type: moreFilterConfig['type'] || 'select' }, operator: { value: 'equals', label: 'is equal to any of', requiresValue: true, multiValue: true }, value: checkedValues.reduce((acc, val) => ({ ...acc, [val]: true }), {}) });
          }
        }
      }
    });

    // Create final filter structure
    let finalFilters = {};
    let filterDefinition = { filterGroups: [], groupOperator: 'AND' };

    if (filters.length > 0 || filterGroups.length > 0) {
      const allGroups = [...filterGroups];
      if (filters.length > 0) allGroups.push({ operator: 'AND', filters });
      filterDefinition = { filterGroups: allGroups, groupOperator: 'AND' };
      finalFilters = buildMongoQuery(allGroups, 'AND');
    }

    // Store both the filter definition and the compiled MongoDB query (skip Redux if isolate mode)
    if (!isolate) {
      dispatch(setQuickFilters({ objectType, filters: filterDefinition }));
      dispatch(applyFilters({ objectType, filters: finalFilters }));
    }

    onGetData({ objectType, filters: finalFilters, filterDefinition });
  };

  // Pipeline specific handlers
  const handlePipelineEdit = () => {
    if (dropdownValues['pipeline']) {
      dispatch(setPipelineSettingsModal(true));
      dispatch(getPipelineById(dropdownValues['pipeline']));
    }
    setDropdownOpen({ ...dropdownOpen, pipeline: false });
  };

  const handlePipelineAdd = () => {
    dispatch(setCreatePipelineModal(true));
    setDropdownOpen({ ...dropdownOpen, pipeline: false });
  };

  // Get date range filter
  const getDateRangeFilter = (field, range) => {
    const now = new Date();
    let startDate, endDate;

    switch (range) {
      case 'Today':
        startDate = new Date(now.setHours(0, 0, 0, 0));
        endDate = new Date(now.setHours(23, 59, 59, 999));
        break;
      case 'Yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday.setHours(0, 0, 0, 0));
        endDate = new Date(yesterday.setHours(23, 59, 59, 999));
        break;
      case 'ThisWeek':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        startDate = new Date(weekStart.setHours(0, 0, 0, 0));
        endDate = new Date();
        break;
      case 'LastWeek':
        const lastWeekEnd = new Date(now);
        lastWeekEnd.setDate(now.getDate() - now.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        startDate = new Date(lastWeekStart.setHours(0, 0, 0, 0));
        endDate = new Date(lastWeekEnd.setHours(23, 59, 59, 999));
        break;
      case 'ThisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date();
        break;
      case 'LastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      default:
        return null;
    }

    return { property: { name: field, type: 'date' }, operator: { value: 'between', label: 'is between', requiresValue: true, isBetween: true }, value: { min: startDate, max: endDate } };
  };

  // Get dropdown options
  const getDropdownOptions = (dropdownConfig) => {
    if (dropdownConfig['options'] === 'dynamic') {
      switch (dropdownConfig['key']) {
        case 'pipeline':
          return pipelines.map(p => p && ({ value: p['_id'], label: p['name'], icon: <span className='fa fa-bolt'></span> }));
        default:
          return [];
      }
    }
    return dropdownConfig['options'] || [];
  };

  // Get checkbox options
  const getCheckboxOptions = (checkboxConfig) => {
    if (checkboxConfig['options'] === 'dynamic') {
      switch (checkboxConfig['key']) {
        case 'owner':
          // Always start with Me and Unassigned options
          const userOptions = [
            { value: userDetails?.['_id'] || userDetails?.['userId'] || '', label: 'Me' },
            { value: '', label: 'Unassigned' }
          ];

          // Get members from either members array or currentWorkspace.members
          const workspaceMembers = members?.length > 0 ? members : currentWorkspace?.['members'] || [];
          const currentUserId = userDetails?.['_id'] || userDetails?.['userId'];

          if (workspaceMembers.length > 0 && currentUserId) {
            // Filter out the current user from members list
            const filteredMembers = workspaceMembers.filter(member => {
              const memberId = member['_id'] || member['userId'];
              return memberId && memberId !== currentUserId;
            });

            // Add other workspace members
            userOptions.push(...filteredMembers.map(member => {
              const firstName = member['firstName'] || member['name'] || '';
              const lastName = member['lastName'] || '';
              const fullName = `${firstName} ${lastName}`.trim();

              return {
                value: member['_id'] || member['userId'] || '',
                label: fullName || member['email'] || 'Unknown'
              };
            }));
          }
          return userOptions;
        case 'tags':
          // Get tag options from the tags property's fieldConfig.options (single source of truth)
          const schema = constants.getObjectSchema(objectType);
          if (schema) {
            const stateKey = schema.type;
            const objectState = { deals, contacts, companies }[stateKey];
            const properties = objectState?.properties || {};
            // Find the tags property across all property groups
            for (const group of Object.values(properties)) {
              if (!Array.isArray(group)) continue;
              const tagsProp = group.find(p => p.name === 'tags');
              if (tagsProp?.fieldConfig?.options) {
                return tagsProp.fieldConfig.options
                  .filter(opt => opt.value && opt.label)
                  .map(opt => ({ value: opt.value, label: opt.label, color: opt.color }));
              }
            }
          }
          return [];
        default:
          return [];
      }
    }
    return checkboxConfig['options'] || [];
  };

  return (
    <div className='header-table-filters advanced-table-filters'>
      <div className='left-area'>
        <div className='filter-box'>
          {/* Search Box */}
          {config['search'] && (
            <div className='searchbox'>
              <TextField
                fullWidth
                hiddenLabel
                size="small"
                variant="standard"
                value={searchText}
                placeholder={config['search']['placeholder']}
                onChange={(e) => setSearchText(e['target']['value'])}
              />
              <i className='icon'>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_224_579)">
                    <path d="M17.8568 16.2345L13.203 11.5807C14.0782 10.377 14.5958 8.8965 14.5958 7.2975C14.5958 3.27375 11.322 0 7.2975 0C3.27375 0 0 3.27375 0 7.2975C0 11.322 3.27375 14.595 7.2975 14.595C8.823 14.595 10.2397 14.1247 11.4128 13.3215L16.0912 18L17.8568 16.2345ZM2.1405 7.2975C2.1405 4.4535 4.45425 2.13975 7.29825 2.13975C10.1423 2.13975 12.456 4.4535 12.456 7.2975C12.456 10.1415 10.1423 12.4552 7.29825 12.4552C4.4535 12.4552 2.1405 10.1415 2.1405 7.2975Z" fill="#89868D" />
                  </g>
                  <defs>
                    <clipPath id="clip0_224_579">
                      <rect width="18" height="18" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </i>
            </div>
          )}

          {/* Dropdown Filters */}
          {config['dropdowns']?.map((dropdown) => {
            const options = getDropdownOptions(dropdown);
            const filteredOptions = dropdownSearches[dropdown['key']] ? options.filter(opt => opt['label'].toLowerCase().includes(dropdownSearches[dropdown['key']].toLowerCase())) : options;

            return (
              <FormControl key={dropdown['key']}>
                <Select
                  displayEmpty
                  MenuProps={{ disableAutoFocusItem: true }}
                  open={dropdownOpen[dropdown['key']] || false}
                  value={dropdownValues[dropdown['key']] || ''}
                  onOpen={() => setDropdownOpen({ ...dropdownOpen, [dropdown['key']]: true })}
                  onClose={() => setDropdownOpen({ ...dropdownOpen, [dropdown['key']]: false })}
                  onChange={(e) => {
                    const newValue = e['target']['value'];
                    if (dropdown['key'] === 'pipeline') dispatch(setPipelineBoard({ stages: [] }));
                    setDropdownValues({ ...dropdownValues, [dropdown['key']]: newValue });
                  }}
                  // Ensure internal padding so caret does not overlap text
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    '.MuiSelect-select': {
                      paddingRight: '46px !important',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      maxWidth: '100%',
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      '@media (max-width: 768px)': {
                        paddingRight: '40px !important', // Increased to accommodate larger gap
                        fontSize: '12px',
                        gap: '4px'
                      },
                      '@media (max-width: 480px)': {
                        paddingRight: '36px !important', // Increased to accommodate larger gap
                        fontSize: '11px',
                        gap: '3px'
                      }
                    },
                    '.MuiSelect-icon': {
                      right: '10px',
                      marginLeft: '12px', // Add more gap between text and icon
                      '@media (max-width: 768px)': {
                        right: '8px',
                        marginLeft: '10px',
                        fontSize: '18px'
                      },
                      '@media (max-width: 480px)': {
                        right: '6px',
                        marginLeft: '8px',
                        fontSize: '16px'
                      }
                    },
                    '@media (max-width: 768px)': {
                      minWidth: '120px !important'
                    },
                    '@media (max-width: 480px)': {
                      minWidth: '100px !important'
                    }
                  }}
                  renderValue={(selected) => {
                    if (!selected) return dropdown['placeholder'];
                    const selectedOption = options.find((option) => option['value'] === selected);
                    return selectedOption ? (
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        maxWidth: '100%',
                        minWidth: 0,
                        width: '100%',
                        overflow: 'hidden'
                      }}>
                        {dropdown['icon'] && selectedOption['icon'] && <i style={{ marginRight: "6px", flexShrink: 0 }}>{selectedOption['icon']}</i>}
                        <Typography sx={{
                          fontSize: { xs: "11px", sm: "12px", md: "14px" },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          minWidth: 0,
                          width: 0, // Force ellipsis to work by setting width to 0 and using flex: 1
                          flex: '1 1 0%'
                        }}>
                          {selectedOption['label']}
                        </Typography>
                      </Box>
                    ) : null;
                  }}
                >
                  <MenuItem disableRipple>
                    <TextField
                      fullWidth
                      autoFocus
                      variant="standard"
                      placeholder="Search..."
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      value={dropdownSearches[dropdown['key']] || ''}
                      onChange={(e) => setDropdownSearches({ ...dropdownSearches, [dropdown['key']]: e['target']['value'] })}
                    />
                  </MenuItem>

                  {dropdownValues[dropdown['key']] && dropdown['key'] !== 'pipeline' && (
                    <MenuItem
                      value=""
                      onClick={() => {
                        setDropdownValues({ ...dropdownValues, [dropdown['key']]: '' });
                        setDropdownOpen({ ...dropdownOpen, [dropdown['key']]: false });
                      }}
                      sx={{ color: 'var(--primary-color)', fontWeight: 500 }}
                    >
                      <i className="fa fa-times" style={{ marginRight: '6px' }}></i>
                      Clear selection
                    </MenuItem>
                  )}

                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                      option && <MenuItem key={option['value']} value={option['value']}>
                        {dropdown['icon'] && option['icon'] && <i style={{ marginRight: "6px" }}>{option['icon']}</i>}
                        {option['label']}
                      </MenuItem>
                    ))
                  ) : <MenuItem disabled>No results found</MenuItem>}

                  {dropdown['key'] === 'pipeline' && (
                    <>
                      <MenuItem onClick={handlePipelineEdit}>
                        <i style={{ marginRight: "6px" }} className="fa fa-edit"></i>
                        Edit pipeline
                      </MenuItem>
                      <MenuItem onClick={handlePipelineAdd}>
                        <i style={{ marginRight: "6px" }} className="fa fa-plus"></i>
                        Add pipeline
                      </MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
            );
          })}

        </div>
      </div>
      <div className='right-deals-filtes'>
        {/* Checkbox Filters (e.g., Deal Owner, Tags) */}
        {config['checkboxFilters']?.map((filter) => {
          const options = getCheckboxOptions(filter);
          const searchTerm = checkboxSearches[filter['key']] || '';
          const filteredOptions = options.filter(opt => opt['label'].toLowerCase().includes(searchTerm.toLowerCase()));

          // Special handling for tags filter
          if (filter['key'] === 'tags') {
            return (
              <div
                key={filter['key']}
                className={`filter-deals-owner ${Object.values(checkboxValues[filter['key']] || {}).some(Boolean)
                  ? 'has-selection'
                  : ''
                  }`}
              >
                <Tooltip
                  title={
                    checkboxValues[filter['key']]
                      ? Object.keys(checkboxValues[filter['key']])
                        .filter((key) => checkboxValues[filter['key']][key])
                        .join(', ') || 'No tags selected'
                      : 'No tags selected'
                  }
                  placement="bottom"
                  arrow
                >
                  <button
                    className="setting-btn"
                    onClick={(e) =>
                      setCheckboxAnchors({
                        ...checkboxAnchors,
                        [filter['key']]: e.currentTarget
                      })
                    }
                  >
                    {/* ✅ Count */}
                    <span className="mr-1">
                      {(() => {
                        const selectedCount = checkboxValues[filter['key']]
                          ? Object.values(checkboxValues[filter['key']]).filter(Boolean).length
                          : 0;
                        return selectedCount > 0 ? `(${selectedCount})` : '';
                      })()}
                    </span>

                    {/* Label */}
                    {filter['label']}
                    <i className="fa fa-angle-down ml-1"></i>

                    {/* ✅ Show Clear only if selected */}
                    {Object.values(checkboxValues[filter['key']] || {}).some(Boolean) && (
                      <button
                        className="btn-clear ml-2"
                        onClick={(e) => {
                          e.stopPropagation(); // prevent reopening menu
                          setCheckboxValues({ ...checkboxValues, [filter['key']]: {} });
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="path-1-inside-1_1030_1913" fill="white"><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z"></path></mask><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)"></path><path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)"></path></svg>
                      </button>
                    )}
                  </button>
                </Tooltip>

                <Menu
                  anchorEl={checkboxAnchors[filter['key']]}
                  open={Boolean(checkboxAnchors[filter['key']])}
                  onClose={() =>
                    setCheckboxAnchors({ ...checkboxAnchors, [filter['key']]: null })
                  }
                  slotProps={{
                    paper: {
                      className: 'custom-dropdown-menu-tags',
                      // style: {
                      //   maxHeight: 300,
                      //   width: 250
                      // }
                    }
                  }}
                >
                  {/* ✅ Header with actions */}
                  <div className="menu-header-select-tags">
                    <button
                      className="btn-small"
                      onClick={() => {
                        // Select All
                        const newValues = {};
                        filteredOptions.forEach((opt) => (newValues[opt.value] = true));
                        setCheckboxValues({ ...checkboxValues, [filter['key']]: newValues });
                      }}
                    >
                      Select All
                    </button>
                    <button
                      className="btn-small ml-2"
                      onClick={() => {
                        // Unselect All
                        setCheckboxValues({ ...checkboxValues, [filter['key']]: {} });
                      }}
                    >
                      Clear
                    </button>
                  </div>

                  <TagsFilter
                    objectType={objectType}
                    selectedTags={Object.keys(checkboxValues[filter['key']] || {}).filter(
                      (key) => checkboxValues[filter['key']][key]
                    )}
                    onTagsChange={(tags) => {
                      const newValues = {};
                      tags.forEach((tag) => {
                        newValues[tag] = true;
                      });
                      setCheckboxValues({ ...checkboxValues, [filter['key']]: newValues });
                    }}
                    searchTerm={checkboxSearches[filter['key']] || ''}
                    onSearchChange={(value) =>
                      setCheckboxSearches({ ...checkboxSearches, [filter['key']]: value })
                    }
                  />
                </Menu>
              </div>
            );
          }

          return (
            <div
              key={filter['key']}
              className={`filter-deals-owner ${Object.keys(checkboxValues[filter['key']] || {} || {}).some(Boolean)
                ? 'has-selection'
                : ''
                }`}
            >
              <Tooltip
                title={
                  checkboxValues[filter['key']]
                    ? (() => {
                      const selectedIds = Object.keys(checkboxValues[filter['key']])
                        .filter((key) => checkboxValues[filter['key']][key]);

                      if (selectedIds.length === 0) return filter['key'] === 'owner' ? 'No owner selected' : 'No selection';

                      // For owner filter, map IDs to names
                      if (filter['key'] === 'owner') {
                        const ownerOptions = getCheckboxOptions(filter);
                        const selectedNames = selectedIds.map(id => {
                          const option = ownerOptions.find(opt => opt.value === id);
                          return option ? option.label : id;
                        });
                        return selectedNames.join(', ');
                      }
                      // For other filters, just return the IDs
                      return selectedIds.join(', ');
                    })()
                    : filter['key'] === 'owner' ? 'No owner selected' : 'No selection'
                }
                placement="bottom"
                arrow
              >
                <button
                  className="setting-btn"
                  onClick={(e) =>
                    setCheckboxAnchors({
                      ...checkboxAnchors,
                      [filter['key']]: e.currentTarget
                    })
                  }
                >
                  {/* ✅ Count */}
                  <span className="mr-1">
                    {(() => {
                      const selectedCount = checkboxValues[filter['key']]
                        ? Object.values(checkboxValues[filter['key']]).filter(Boolean).length
                        : 0;
                      return selectedCount > 0 ? `(${selectedCount})` : '';
                    })()}
                  </span>

                  {/* Label */}
                  {filter['label']}
                  <i className="fa fa-angle-down ml-1"></i>

                  {/* ✅ Show Clear only if selected */}
                  {Object.values(checkboxValues[filter['key']] || {}).some(Boolean) && (
                    <button
                      className="btn-clear ml-2"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent reopening menu
                        setCheckboxValues({ ...checkboxValues, [filter['key']]: {} });
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="path-1-inside-1_1030_1913" fill="white"><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z"></path></mask><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)"></path><path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)"></path></svg>
                    </button>
                  )}
                </button>
              </Tooltip>

              <Menu
                anchorEl={checkboxAnchors[filter['key']]}
                open={Boolean(checkboxAnchors[filter['key']])}
                onClose={() =>
                  setCheckboxAnchors({ ...checkboxAnchors, [filter['key']]: null })
                }
              >
                {/* ✅ Header with actions */}
                <div className="menu-header-select-tags">
                  <button
                    className="btn-small"
                    onClick={() => {
                      // Select All
                      const newValues = {};
                      filteredOptions.forEach((opt) => (newValues[opt.value] = true));
                      setCheckboxValues({ ...checkboxValues, [filter['key']]: newValues });
                    }}
                  >
                    Select All
                  </button>
                  <button
                    className="btn-small ml-2"
                    onClick={() => {
                      // Unselect All
                      setCheckboxValues({ ...checkboxValues, [filter['key']]: {} });
                    }}
                  >
                    Clear
                  </button>
                </div>

                <MenuItem>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={searchTerm}
                    placeholder="Search"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      setCheckboxSearches({
                        ...checkboxSearches,
                        [filter['key']]: e.target.value,
                      })
                    }
                  />
                </MenuItem>

                {filteredOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => {
                      const currentValues = checkboxValues[filter['key']] || {};
                      setCheckboxValues({
                        ...checkboxValues,
                        [filter['key']]: {
                          ...currentValues,
                          [option.value]: !currentValues[option.value],
                        },
                      });
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={checkboxValues[filter['key']]?.[option.value] || false}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' }, '& .MuiSvgIcon-root': { fill: 'var(--primary-color)' }, '&.Mui-checked .MuiSvgIcon-root': { fill: 'var(--primary-color)', color: 'var(--primary-color)' }, '&.Mui-checked .MuiSvgIcon-root path': { fill: 'var(--primary-color)' } }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Menu>
            </div>
          );
        })}

        {/* Date Filters */}
        {config['dateFilters']?.map((filter) => {
          const selectedKey = dateValues[filter['key']];
          const selectedOption = dateRangeOptions.find((opt) => opt['key'] === selectedKey);

          return (
            <div key={filter['key']} className={`filter-deals-owner ${dateValues[filter['key']] ? 'has-selection' : ''}`}>
              <button
                className='setting-btn'
                onClick={(e) =>
                  setDateAnchors({ ...dateAnchors, [filter['key']]: e.currentTarget })
                }
              >
                {/* Show selected label or fallback */}
                {dateValues[filter['key']]
                  ? dateRangeOptions.find(opt => opt.key === dateValues[filter['key']])?.label
                  : filter['label']}

                <i className='fa fa-angle-down ml-1'></i>

                {/* Show clear (x) only when something is selected */}
                {dateValues[filter['key']] && (
                  <button
                    className="btn-clear ml-2"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent menu open
                      const updated = { ...dateValues };
                      delete updated[filter['key']];
                      setDateValues(updated);
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="path-1-inside-1_1030_1913" fill="white"><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z"></path></mask><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)"></path><path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)"></path></svg>
                  </button>
                )}
              </button>

              <Menu
                anchorEl={dateAnchors[filter['key']]}
                open={Boolean(dateAnchors[filter['key']])}
                onClose={() =>
                  setDateAnchors({ ...dateAnchors, [filter['key']]: null })
                }
              >
                {dateRangeOptions.map((option) => (
                  <MenuItem
                    key={option['key']}
                    onClick={() => {
                      if (dateValues[filter['key']] === option['key']) {
                        // unselect (reset)
                        const updated = { ...dateValues };
                        delete updated[filter['key']];
                        setDateValues(updated);
                      } else {
                        setDateValues({ ...dateValues, [filter['key']]: option['key'] });
                      }
                      setDateAnchors({ ...dateAnchors, [filter['key']]: null });
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={dateValues[filter['key']] === option['key']}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' }, '& .MuiSvgIcon-root': { fill: 'var(--primary-color)' }, '&.Mui-checked .MuiSvgIcon-root': { fill: 'var(--primary-color)', color: 'var(--primary-color)' }, '&.Mui-checked .MuiSvgIcon-root path': { fill: 'var(--primary-color)' } }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={option['label']} />
                  </MenuItem>
                ))}
              </Menu>
            </div>

          );
        })}


        {/* Dynamic More Filters */}
        {Object.keys(moreFilterAnchors).map((filterKey) => {
          const filterConfig = config['moreFilters']?.[filterKey];
          if (!filterConfig) return null;
          const anchor = moreFilterAnchors[filterKey] === 'pending' ? null : moreFilterAnchors[filterKey];

          // Get options for this filter
          let options = [];
          if (filterConfig['options'] === 'dynamic') {
            // Dynamic options based on filter key
            if (filterKey === 'stage') {
              const selectedPipeline = pipelines.find(p => p['_id'] === dropdownValues['pipeline']);
              options = selectedPipeline?.['stages']?.map(stage => ({ value: stage['id'], label: stage['name'] })) || [];
            }
          } else if (filterConfig['options']) {
            // Use predefined options from config
            options = filterConfig['options'];
          }

          const searchTerm = moreFilterSearches[filterKey] || '';
          const filteredOptions = options.filter(opt => opt['label'].toLowerCase().includes(searchTerm.toLowerCase()));


          return (
            <div
              key={filterKey}
              className={`filter-deals-owner ${Object.values(moreFilterValues[filterKey] || {}).some(Boolean)
                ? 'has-selection'
                : ''
                }`}
            >
              <button
                className="setting-btn"
                onClick={(e) =>
                  setMoreFilterAnchors({
                    ...moreFilterAnchors,
                    [filterKey]: e.currentTarget
                  })
                }
              >
                {/* ✅ Count */}
                <span className="mr-1">
                  {(() => {
                    const selectedCount = moreFilterValues[filterKey]
                      ? Object.values(moreFilterValues[filterKey]).filter(Boolean).length
                      : 0;
                    return selectedCount > 0 ? `(${selectedCount})` : '';
                  })()}
                </span>

                {/* Label */}
                {filterConfig['label']}
                <i className="fa fa-angle-down ml-1"></i>

                {/* ✅ Show Clear only if selection exists */}
                {Object.values(moreFilterValues[filterKey] || {}).some(Boolean) && (
                  <button
                    className="btn-clear ml-2"
                    onClick={(e) => {
                      e.stopPropagation(); // don’t trigger menu
                      setMoreFilterValues({ ...moreFilterValues, [filterKey]: {} });
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><mask id="path-1-inside-1_1030_1913" fill="white"><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z"></path></mask><path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)"></path><path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)"></path></svg>
                  </button>
                )}
              </button>

              <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => {
                  const newAnchors = { ...moreFilterAnchors };
                  delete newAnchors[filterKey];
                  setMoreFilterAnchors(newAnchors);
                }}
                slotProps={{
                  paper: {
                    style: { maxHeight: 350, width: 280 }
                  }
                }}
              >

                {/* ✅ Header with actions */}
                <div className="menu-header-select-tags">
                  <button
                    className="btn-small"
                    onClick={() => {
                      // Select All
                      const newValues = {};
                      filteredOptions.forEach((opt) => (newValues[opt.value] = true));
                      setMoreFilterValues({ ...moreFilterValues, [filterKey]: newValues });
                    }}
                  >
                    Select All
                  </button>
                  <button
                    className="btn-small ml-2"
                    onClick={() => {
                      // Unselect All
                      setMoreFilterValues({ ...moreFilterValues, [filterKey]: {} });
                    }}
                  >
                    Clear
                  </button>
                </div>

                {/* ✅ Search input */}
                <MenuItem>
                  <TextField
                    variant="standard"
                    placeholder="Search"
                    fullWidth
                    value={searchTerm}
                    onKeyDown={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      setMoreFilterSearches({
                        ...moreFilterSearches,
                        [filterKey]: e.target.value
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </MenuItem>

                {/* ✅ List of options */}
                {filteredOptions.map((option) => (
                  <MenuItem
                    key={option['value']}
                    onClick={() => {
                      const currentValues = moreFilterValues[filterKey] || {};
                      setMoreFilterValues({
                        ...moreFilterValues,
                        [filterKey]: {
                          ...currentValues,
                          [option['value']]: !currentValues[option['value']]
                        }
                      });
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={moreFilterValues[filterKey]?.[option['value']] || false}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        sx={{ color: 'var(--primary-color)', '&.Mui-checked': { color: 'var(--primary-color)' }, '& .MuiSvgIcon-root': { fill: 'var(--primary-color)' }, '&.Mui-checked .MuiSvgIcon-root': { fill: 'var(--primary-color)', color: 'var(--primary-color)' }, '&.Mui-checked .MuiSvgIcon-root path': { fill: 'var(--primary-color)' } }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={option['label']} />
                  </MenuItem>
                ))}

                {/* ✅ Remove filter */}
                <MenuItem
                  onClick={() => {
                    const newAnchors = { ...moreFilterAnchors };
                    delete newAnchors[filterKey];
                    setMoreFilterAnchors(newAnchors);

                    const newValues = { ...moreFilterValues };
                    delete newValues[filterKey];
                    setMoreFilterValues(newValues);
                  }}
                >
                  <Typography sx={{ color: 'error.main', fontSize: '14px' }}>
                    Remove filter
                  </Typography>
                </MenuItem>
              </Menu>
            </div>

          );
        })}

        {/* More Filters */}
        {config['moreFilters'] && (
          <div className='filter-deals-owner'>
            <button className='setting-btn' onClick={(e) => setMoreAnchor(e['currentTarget'])}>
              + More <i className='fa fa-angle-down'></i>
            </button>
            <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
              <p style={{ padding: '0 25px', margin: '0px 16px 5px', fontSize: '12px', color: '#666', fontWeight: '600' }}>
                Add a quick filter
              </p>
              {Object.entries(config['moreFilters']).filter(([key]) => !moreFilterAnchors[key]).map(([key, filterConfig]) => (
                <MenuItem key={key} onClick={() => {
                  // Just mark this filter as selected, the button will be rendered in the next render cycle
                  setMoreFilterAnchors({ ...moreFilterAnchors, [key]: 'pending' });
                  setMoreAnchor(null);
                }}>
                  <ListItemText primary={filterConfig['label']} />
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}

        {/* Advanced Filters Button */}
        {onAdvancedClick && (
          <div className='filter-deals-owner'>
            <button className="btn-fillter" onClick={onAdvancedClick}>
              Advanced filters
              <i className='icon'>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6.4165H18.5" stroke="var(--primary-color)" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M6 11H16" stroke="var(--primary-color)" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M8.99951 15.5835H12.6662" stroke="var(--primary-color)" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </i>
            </button>
          </div>
        )}
      </div>

    </div>
  )
};

export default QuickFilters;